/**
 * <brand-search> — search, filter (animal/food type/min products), and sort
 * controller for the brand ID cards (snippets/brand-card.liquid). Brand grids
 * can be split across several brand-index sections (Shopify caps blocks-per-
 * section at 50), so every operation here reads/writes ALL .brand-card
 * elements on the page and ALL [data-brand-cards-grid] containers, not just
 * the ones inside its own section — filtering/sorting must feel like a
 * single continuous grid to the customer.
 *
 * Sorting reorders each grid's cards in place (client-side DOM reordering)
 * rather than moving cards between sections, so a brand always stays within
 * its original section's grid container — only the order within each grid
 * changes, plus visibility (hidden/shown) per the active filters.
 */
class BrandSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.brand-search__input');
    this.sortSelect = this.querySelector('[data-brand-sort]');
    this.disclosures = Array.from(this.querySelectorAll('.brand-toolbar__disclosure'));
    this.animalCheckboxes = Array.from(this.querySelectorAll('[data-brand-filter-animal]'));
    this.foodCheckboxes = Array.from(this.querySelectorAll('[data-brand-filter-food]'));
    this.minProductsInput = this.querySelector('[data-brand-min-products]');
    this.minProductsValue = this.querySelector('[data-brand-min-products-value]');
    this.clearButton = this.querySelector('[data-brand-filters-clear]');

    this.cards = Array.from(document.querySelectorAll('.brand-card[data-brand-name]'));
    this.grids = Array.from(document.querySelectorAll('[data-brand-cards-grid]'));

    // cache each card's containing wrapper + parsed data once, up front
    this.entries = this.cards.map((card) => ({
      card,
      wrapper: card.closest('[data-brand-cards-grid] > *') || card.parentElement,
      grid: card.closest('[data-brand-cards-grid]'),
      name: card.getAttribute('data-brand-name') || '',
      productCount: parseInt(card.getAttribute('data-product-count'), 10) || 0,
      popularity: parseInt(card.getAttribute('data-popularity'), 10) || 1,
      animals: (card.getAttribute('data-animals') || '').split(',').filter(Boolean),
      foodTypes: (card.getAttribute('data-food-types') || '').split(',').filter(Boolean),
    }));

    if (this.input) this.input.addEventListener('input', this.apply.bind(this));
    if (this.sortSelect) this.sortSelect.addEventListener('change', this.apply.bind(this));
    this.animalCheckboxes.forEach((cb) => cb.addEventListener('change', this.apply.bind(this)));
    this.foodCheckboxes.forEach((cb) => cb.addEventListener('change', this.apply.bind(this)));
    if (this.minProductsInput) {
      this.minProductsInput.addEventListener('input', () => {
        if (this.minProductsValue) this.minProductsValue.textContent = this.minProductsInput.value;
        this.apply();
      });
    }
    // Only one filter disclosure open at a time (matches the native collection
    // facets UX in snippets/facet-filters.liquid).
    this.disclosures.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        this.disclosures.forEach((other) => {
          if (other !== details) other.open = false;
        });
      });
    });
    document.addEventListener('click', (event) => {
      this.disclosures.forEach((details) => {
        if (details.open && !details.contains(event.target)) details.open = false;
      });
    });
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        if (this.input) this.input.value = '';
        this.animalCheckboxes.forEach((cb) => { cb.checked = false; });
        this.foodCheckboxes.forEach((cb) => { cb.checked = false; });
        if (this.minProductsInput) {
          this.minProductsInput.value = this.minProductsInput.min || 1;
          if (this.minProductsValue) this.minProductsValue.textContent = this.minProductsInput.value;
        }
        if (this.sortSelect) this.sortSelect.value = 'featured';
        this.apply();
      });
    }
  }

  apply() {
    const query = (this.input?.value || '').trim().toLowerCase();
    const activeAnimals = this.animalCheckboxes.filter((cb) => cb.checked).map((cb) => cb.value);
    const activeFoodTypes = this.foodCheckboxes.filter((cb) => cb.checked).map((cb) => cb.value);
    const minProducts = this.minProductsInput ? parseInt(this.minProductsInput.value, 10) || 1 : 1;
    const sortMode = this.sortSelect?.value || 'featured';

    const gridsWithVisibleCard = new Set();

    this.entries.forEach((entry) => {
      const matchesSearch = query === '' || entry.name.includes(query);
      const matchesAnimals = activeAnimals.length === 0 || activeAnimals.every((a) => entry.animals.includes(a));
      const matchesFoodTypes = activeFoodTypes.length === 0 || activeFoodTypes.every((f) => entry.foodTypes.includes(f));
      const matchesMinProducts = entry.productCount >= minProducts;
      const matches = matchesSearch && matchesAnimals && matchesFoodTypes && matchesMinProducts;

      if (entry.wrapper) {
        entry.wrapper.hidden = !matches;
        // Use the wrapper's CURRENT parent, not the cached entry.grid — a
        // prior sort may have moved this card's wrapper into a different
        // grid element than the one it started in (see sortGrids()).
        const currentGrid = entry.wrapper.closest('[data-brand-cards-grid]');
        if (matches && currentGrid) gridsWithVisibleCard.add(currentGrid);
      }
    });

    this.grids.forEach((grid) => {
      const section = grid.closest('.section-brand-index');
      if (!section) return;
      const hasQuery = query !== '' || activeAnimals.length > 0 || activeFoodTypes.length > 0 || minProducts > 1;
      section.hidden = hasQuery && !gridsWithVisibleCard.has(grid);
    });

    if (sortMode !== 'featured') {
      this.sortGrids(sortMode);
    } else {
      this.restoreOriginalOrder();
    }
  }

  sortGrids(mode) {
    // Sort the FULL cross-section list of entries first (one continuous A-Z or
    // most-popular-first order across all 181 brands), then redistribute the
    // sorted wrappers back into the grids in order — each grid keeps the same
    // NUMBER of cards it started with (so the 50/50/50/31 chunk split and its
    // "leftover row" card-width math still lines up), it just contains a
    // different slice of the globally-sorted sequence.
    const sorted = this.entries.slice().sort((a, b) => {
      if (mode === 'alphabetical') {
        return a.name.localeCompare(b.name);
      }
      if (mode === 'popularity') {
        return b.popularity - a.popularity || a.name.localeCompare(b.name);
      }
      return 0;
    });

    let cursor = 0;
    this.grids.forEach((grid) => {
      const capacity = this.entries.filter((e) => e.grid === grid).length;
      const slice = sorted.slice(cursor, cursor + capacity);
      slice.forEach((entry) => grid.appendChild(entry.wrapper));
      cursor += capacity;
    });
  }

  restoreOriginalOrder() {
    this.grids.forEach((grid) => {
      const originalOrder = this.entries
        .filter((e) => e.grid === grid)
        .map((e) => e.wrapper);
      originalOrder.forEach((wrapper) => grid.appendChild(wrapper));
    });
  }
}

customElements.define('brand-search', BrandSearch);
